import { memo, RefObject, useEffect, useMemo } from 'react'
import * as Styled from './styled'
import { useDropdownState } from 'hooks/useDropdownState'

import { TtsItemContext } from './contextApi'

import TtsItemText from './Text'
import TtsItemControlsButtons from './ControlsButtons'
import TtsItemSpeakerFlyout from './SpeakerFlyout'
import { useScrollIntoView } from 'hooks/useScrollIntoView'
import { ITtsItemData } from 'types/text-to-speech'
import { useGetTtsItemDraftState, useGetTtsItemLoadingState } from '../../hooks'
import { useGetTtsItemSpeakerData } from 'containers/SubSidebar/shared/Voices/hooks'
import { dispatch } from 'store'
import { voiceCatalogThunks } from 'store/features/voiceCatalog/thunks'
import LoadingSkeleton from 'common-components/LoadingSkeleton'

interface IProps {
  ttsData: ITtsItemData
  onClick: (id: number, isDraft: boolean) => void
  isActive: boolean
  scrollableParentRef?: RefObject<HTMLDivElement | null>
}

const TtsListItem: React.FC<IProps> = ({
  ttsData,
  onClick,
  isActive,
  scrollableParentRef
}: IProps) => {
  const { text, id, speakerId, langCode } = ttsData
  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState()
  const { node, setNode } = useScrollIntoView()

  const isDraft = useGetTtsItemDraftState(id)
  const currentSpeaker = useGetTtsItemSpeakerData(speakerId, langCode)
  const isLoading = useGetTtsItemLoadingState(id)

  const isSpeakerIdChanged = useMemo(
    () => currentSpeaker?.id !== speakerId,
    [currentSpeaker, speakerId]
  )

  const handleClick = () => {
    onClick(id, isDraft)
  }

  useEffect(() => {
    if (isActive && isSpeakerIdChanged) {
      dispatch(voiceCatalogThunks.getSpeakersPerLangCode(langCode))
    }
  }, [isActive, langCode, isSpeakerIdChanged])

  return (
    <TtsItemContext.Provider
      value={{
        text,
        id,
        scrollableParentRef,
        toggleSpeakerFlyout: toggleDropdown
      }}
    >
      <Styled.TtsListItemWrapper
        ref={isActive ? setNode : null}
        onClick={handleClick}
        $isActive={isActive}
        $isLoading={isLoading}
      >
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <TtsItemControlsButtons isActive={isActive} />
            <TtsItemText isActive={isActive} />
          </>
        )}
      </Styled.TtsListItemWrapper>
      {isOpen && (
        <TtsItemSpeakerFlyout
          node={node}
          closeDropdown={closeDropdown}
          isOpen={isOpen}
        />
      )}
    </TtsItemContext.Provider>
  )
}

export default memo(TtsListItem)
